import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Navigation, Crown, ChevronRight, X, Smartphone } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useConsumerAuth } from "@/consumer/ConsumerAuth";

const LOGO = "https://static.prod-images.emergentagent.com/jobs/301a1c64-0b19-462c-acf8-1e89c9bc05f1/images/cba60e89c5f18aec88b7b5e4bb560a3a3cd16137942d429c9dc4648d7082b353.png";
const POGZILLA = "/pogzilla.png";

const SLIDES = [
  { img: POGZILLA, color: "text-emerald-400", bg: "bg-emerald-500/15", tKey: "s1" },
  { icon: MapPin, color: "text-sky-400", bg: "bg-sky-500/15", tKey: "s2" },
  { icon: Navigation, color: "text-emerald-400", bg: "bg-emerald-500/15", tKey: "s3" },
  { icon: Smartphone, color: "text-orange-400", bg: "bg-orange-500/15", tKey: "s5" },
  { icon: Crown, color: "text-amber-400", bg: "bg-amber-500/15", tKey: "s4" },
];

export default function Onboarding() {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [i, setI] = useState(0);
  const { driver, loginGoogle } = useConsumerAuth() || {};

  useEffect(() => {
    // Don't show the tour over a shared deal deep-link (the billboard panel + its
    // link buttons must be immediately tappable for first-time visitors).
    if (window.location.pathname.startsWith("/d/") || window.location.pathname.startsWith("/pog/")) return;
    if (!localStorage.getItem("airpog_onboarded")) setShow(true);
  }, []);

  const done = () => { localStorage.setItem("airpog_onboarded", "1"); setShow(false); };
  const next = () => (i < SLIDES.length - 1 ? setI(i + 1) : done());
  const startTrial = () => { localStorage.setItem("airpog_onboarded", "1"); loginGoogle && loginGoogle(); };

  if (!show) return null;
  const S = SLIDES[i];
  const Icon = S.icon;
  const isLast = i === SLIDES.length - 1;
  const showTrialCta = isLast && !driver?.is_plus;

  return (
    <AnimatePresence>
      <motion.div
        data-testid="onboarding-overlay"
        className="fixed inset-0 z-[4000] flex items-end sm:items-center justify-center bg-slate-950/80 backdrop-blur-sm p-4"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      >
        <motion.div
          initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
          className="glass-strong rounded-3xl w-full max-w-md p-6 relative"
        >
          <button data-testid="onboarding-skip" onClick={done} className="absolute top-4 right-4 w-9 h-9 rounded-xl hover:bg-slate-700/60 flex items-center justify-center text-slate-400">
            <X className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-2.5 mb-6">
            <img src={LOGO} alt="AIR.POG" className="w-9 h-9 rounded-xl object-contain" />
            <div className="leading-tight">
              <span className="font-heading font-extrabold tracking-tight bg-gradient-to-r from-sky-400 to-violet-400 bg-clip-text text-transparent">AIR.POG</span>
              <p className="text-[11px] text-slate-400 font-medium">Just POG it.</p>
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={i}
              initial={{ x: 30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: -30, opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              {S.img ? (
                <img src={S.img} alt="POGZILLA" className="w-24 h-24 object-contain mb-4 drop-shadow-[0_0_20px_rgba(52,211,153,0.45)]" />
              ) : (
                <div className={`w-16 h-16 rounded-2xl ${S.bg} flex items-center justify-center mb-4`}>
                  <Icon className={`w-8 h-8 ${S.color}`} />
                </div>
              )}
              <h2 className="font-heading text-2xl font-bold leading-tight mb-2 text-white">{t(`onboard2.${S.tKey}t`)}</h2>
              <p className="text-slate-400 text-sm leading-relaxed">{t(`onboard2.${S.tKey}b`)}</p>
            </motion.div>
          </AnimatePresence>

          <div className="flex items-center justify-between mt-8">
            <div className="flex gap-1.5">
              {SLIDES.map((_, idx) => (
                <span key={idx} className={`h-1.5 rounded-full transition-all ${idx === i ? "w-6 bg-sky-400" : "w-1.5 bg-slate-600"}`} />
              ))}
            </div>
            {showTrialCta ? (
              <button
                data-testid="onboarding-start-trial"
                onClick={startTrial}
                className="h-11 px-5 rounded-xl bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 font-heading font-bold text-sm flex items-center gap-1.5 hover:opacity-90"
              >
                <Crown className="w-4 h-4" /> {t("onboard2.trial")}
              </button>
            ) : (
              <button
                data-testid="onboarding-next"
                onClick={next}
                className="h-11 px-5 rounded-xl bg-gradient-to-r from-sky-500 to-violet-500 text-white font-heading font-bold text-sm flex items-center gap-1.5 hover:opacity-90"
              >
                {isLast ? t("onboard2.explore") : t("onboard2.next")}
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
          {showTrialCta && (
            <button data-testid="onboarding-just-drive" onClick={done} className="w-full mt-3 text-center text-xs text-slate-400 hover:text-slate-200 font-semibold">
              {t("onboard2.freeExplore")}
            </button>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
